import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { GlobeIcon, MapPinIcon } from 'lucide-react';
import { CARE_LEVEL_LABELS, specialisationLabel, type Provider } from '@kennisbank/shared';
import { IntakeControl } from './IntakeControl.js';

const TYPE_LABELS: Record<Provider['type'], string> = {
  ggz_instelling: 'GGZ-instelling',
  vrijgevestigd: 'Vrijgevestigd',
  groepspraktijk: 'Groepspraktijk',
  e_health: 'E-health',
  overig: 'Overig',
};

/**
 * Zorgniveau + "Zonder verwijzing" als kleine labels. Leeg betekent onbekend,
 * niet "geen": dan tonen we niets in plaats van een misleidend "nee".
 */
export function CareLevelPills({
  provider,
  className = '',
}: {
  provider: Provider;
  className?: string;
}): JSX.Element | null {
  const levels = provider.careLevels ?? [];
  if (levels.length === 0 && !provider.noReferralNeeded) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {levels.map((c) => (
        <span
          key={c}
          className="rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-medium text-brand-700"
        >
          {CARE_LEVEL_LABELS[c]}
        </span>
      ))}
      {provider.noReferralNeeded && (
        <span className="rounded-full border border-brand-200 bg-white px-2 py-0.5 text-[10.5px] font-medium text-brand-700">
          Zonder verwijzing
        </span>
      )}
    </div>
  );
}

/**
 * Sociale Kaart map view. Renders one pin per provider that carries
 * `location.coordinates`; providers without coordinates (e.g. e-health
 * platforms) are listed below the map as "geen kaartlocatie".
 *
 * Brand-aqua teardrop pin built as an L.divIcon so we avoid Leaflet's
 * default marker PNGs (whose asset paths break under Vite bundling).
 */
const BRAND_AQUA = '#269191';

// Centre of the Netherlands — the initial view before bounds are fitted.
const NL_CENTER: [number, number] = [52.15, 5.4];

const pinIcon = L.divIcon({
  className: 'suppohrt-map-pin',
  html: `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 13.4 23.6 14 24.2.6.6 1.4.6 2 0 .6-.6 14-13.7 14-24.2C30 6.7 23.3 0 15 0z" fill="${BRAND_AQUA}"/>
    <circle cx="15" cy="15" r="6" fill="#ffffff"/>
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -36],
});

/**
 * Brand-aqua cluster bubble (monochrome, size-tiered) instead of
 * markercluster's default blue/green/yellow — petrol/mosterd stay reserved for
 * the Ring chips. Clicking a cluster zooms in; at max zoom the many providers
 * sharing one town-centroid (the geocoder's plaats-niveau pins) spiderfy into a
 * clickable ring rather than stacking invisibly.
 */
function clusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  const size = count < 10 ? 36 : count < 50 ? 44 : 52;
  return L.divIcon({
    className: 'suppohrt-cluster-pin',
    html: `<div style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:9999px;background:${BRAND_AQUA};color:#fff;font-weight:700;font-size:${count < 1000 ? 13 : 11}px;border:3px solid rgba(255,255,255,0.92);box-shadow:0 1px 5px rgba(0,0,0,0.35);font-family:Montserrat,system-ui,sans-serif;">${count}</div>`,
    iconSize: [size, size],
  });
}

interface ProviderMapProps {
  providers: Provider[];
  /** Alleen suppohrt_admin ziet de "zonder kaartlocatie"-lijst (databewaking). */
  isAdmin: boolean;
  /**
   * Demo-omgeving (issue #44): uitsluitend clusterbubbels met aantallen op een
   * vastgezet zoomniveau — geen popups, losse pins, zoom of spiderfy. Zo toont
   * de kaart spreiding zonder ooit een praktijknaam of adres prijs te geven.
   */
  demo?: boolean;
}

type ProviderLocation = Provider['location'];
type LocatedPin = {
  provider: Provider;
  loc: ProviderLocation & { coordinates: { lat: number; lng: number } };
  index: number; // 0 = primaire locatie, >0 = extra vestiging
  total: number; // aantal gepinde vestigingen van deze provider
};

/**
 * Eén marker per vestiging: de primaire `location` plus elke `additionalLocations`
 * die coordinates draagt. Multi-locatie-organisaties (Arkin, De Viersprong) tonen
 * zo een pin per vestiging — het meest bruikbaar voor de POH die naar de
 * dichtstbijzijnde locatie verwijst. Een provider zonder enkele gepinde locatie
 * valt in de "zonder kaartlocatie"-lijst onder de kaart.
 */
function pinsOf(p: Provider): LocatedPin[] {
  const all = [p.location, ...(p.additionalLocations ?? [])];
  const withCoords = all.filter((loc) => loc.coordinates != null);
  return withCoords.map((loc, i) => ({
    provider: p,
    loc: loc as LocatedPin['loc'],
    index: i,
    total: withCoords.length,
  }));
}

/**
 * Pans/zooms the map to fit all currently visible pins whenever the set
 * changes (e.g. after a province filter), so the demo follows the filters.
 */
function FitBounds({ points }: { points: Array<[number, number]> }): null {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0]!, 12, { animate: true });
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 13, animate: true });
  }, [map, points]);
  return null;
}

export function ProviderMap({ providers, isAdmin, demo = false }: ProviderMapProps): JSX.Element {
  const pins = useMemo(() => providers.flatMap(pinsOf), [providers]);
  const unlocated = useMemo(() => providers.filter((p) => pinsOf(p).length === 0), [providers]);
  const points = useMemo<Array<[number, number]>>(
    () => pins.map((e) => [e.loc.coordinates.lat, e.loc.coordinates.lng]),
    [pins],
  );

  return (
    <div className="space-y-4">
      {/* isolate: Leaflet zet intern z-index 400-1000; zonder eigen stacking context
          hangt de kaart over het mobiele menu (z-40) heen. */}
      <div className="isolate h-[640px] overflow-hidden rounded-xl border border-brand-100 shadow-card">
        <MapContainer
          center={NL_CENTER}
          zoom={8}
          // Demo: zoomniveau vastgezet — muiswiel, dubbelklik, pinch, toetsen
          // en de +/- knoppen doen niets (min === max maakt elk zoompad dood).
          minZoom={demo ? 8 : undefined}
          maxZoom={demo ? 8 : undefined}
          scrollWheelZoom={!demo}
          doubleClickZoom={!demo}
          touchZoom={!demo}
          boxZoom={!demo}
          keyboard={!demo}
          zoomControl={!demo}
          className="h-full w-full"
          style={{ background: '#e8eef0' }}
        >
          <TileLayer
            // tile.openstreetmap.org weigert apps zonder eigen tegelbron (403 "usage
            // policy"); PDOK BRT is gratis, sleutelloos en dekt heel Nederland.
            attribution='&copy; <a href="https://www.kadaster.nl">Kadaster</a> / <a href="https://www.pdok.nl">PDOK</a>'
            url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          {!demo && <FitBounds points={points} />}
          <MarkerClusterGroup
            iconCreateFunction={clusterIcon}
            showCoverageOnHover={false}
            maxClusterRadius={60}
            spiderfyDistanceMultiplier={1.6}
            // Demo: één praktijk rendert als bubbel "1" (nooit een pin),
            // cluster-klik zoomt niet en spiderfy staat uit.
            singleMarkerMode={demo}
            zoomToBoundsOnClick={!demo}
            spiderfyOnMaxZoom={!demo}
            chunkedLoading
          >
            {pins.map(({ provider: p, loc, index, total }) => (
              <Marker
                key={`${p.id}-${index}`}
                position={[loc.coordinates.lat, loc.coordinates.lng]}
                icon={pinIcon}
                interactive={!demo}
              >
                {!demo && (
                  <Popup maxWidth={320} minWidth={240}>
                    <div className="min-w-[180px]">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-brand-700/70">
                        {TYPE_LABELS[p.type]}
                        {total > 1 && (
                          <span className="ml-1 normal-case text-ink-900/50">
                            · vestiging {index + 1} van {total}
                          </span>
                        )}
                      </div>
                      <div className="text-[14px] font-semibold leading-tight text-ink-900">
                        {p.name}
                      </div>
                      <div className="mt-1 flex items-start gap-1 text-[12px] text-ink-900/70">
                        <MapPinIcon size={12} className="mt-0.5 shrink-0 text-brand-700/60" />
                        <span>
                          {[loc.address, loc.city, loc.province].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                      {/* Zelfde bewerkbare aanmeldstop-strook als op de rasterkaart
                        (incl. reden + einddatum), niet enkel een read-only badge —
                        zo kun je vanaf de kaart de stop ook zetten/opheffen. */}
                      <div className="mt-1.5">
                        <IntakeControl provider={p} />
                      </div>
                      <CareLevelPills provider={p} className="mt-2" />
                      {p.specialisations.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {p.specialisations.slice(0, 4).map((s) => (
                            <span
                              key={s}
                              className="rounded-full border border-brand-200 bg-white px-2 py-0.5 text-[10.5px] font-medium text-ink-800/80"
                            >
                              {specialisationLabel(s)}
                            </span>
                          ))}
                        </div>
                      )}
                      {p.contactInfo.website && (
                        <a
                          href={p.contactInfo.website}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-petrol-500 hover:underline"
                        >
                          <GlobeIcon size={12} /> Website
                        </a>
                      )}
                    </div>
                  </Popup>
                )}
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>

      {/* Demo: praktijken zonder kaartlocatie alleen als aantal — geen namen. */}
      {demo && unlocated.length > 0 && (
        <p className="rounded-lg border border-dashed border-brand-200 bg-brand-50/40 px-4 py-3 text-sm text-ink-900/60">
          {unlocated.length} zonder kaartlocatie (bv. online aanbod)
        </p>
      )}

      {/* Databewaking: alleen admins zien welke aanbieders nog geen kaartlocatie
          hebben. Voor een POH is dit ruis; voor een admin is het de werklijst.
          Native <details> houdt het ingeklapt — geen lange opsomming meer. */}
      {isAdmin && unlocated.length > 0 && (
        <details className="rounded-lg border border-dashed border-brand-200 bg-brand-50/40 px-4 py-3 text-sm text-ink-900/60">
          <summary className="cursor-pointer font-medium text-ink-900/80 marker:text-brand-700/60">
            {unlocated.length} zonder kaartlocatie (bv. online aanbod)
          </summary>
          <div className="mt-2 leading-relaxed">{unlocated.map((p) => p.name).join(', ')}</div>
        </details>
      )}
    </div>
  );
}
